import React from 'react';
import PageHeader from '../components/PageHeader';
import Callout from '../components/Callout';
import { motion } from 'framer-motion';

const Analysis = () => {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="prose-custom">
      <PageHeader 
        badge="Stage 3"
        title="Analysis & Computation" 
        subtitle="The final mile. Ensuring your code, environment, and workflow execute perfectly on another machine."
      />

      <p>
        This is the stage scientists most frequently associate with "reproducibility." If you hand your data and 
        script to a colleague, can they run it without encountering a barrage of errors? Usually, the answer is no, 
        because software dependencies and environments differ wildly between machines.
      </p>

      <h2>1. Version Control (Git)</h2>
      <p>
        Naming files `analysis_final.R`, `analysis_final_v2.R`, and `analysis_really_final.R` is a fragile system. 
        <strong>Git</strong> tracks changes to your code over time, allowing you to branch off to try new ideas, 
        revert to older working versions, and collaborate cleanly with others. Platforms like GitHub or GitLab 
        then serve as the public hub for sharing these repositories.
      </p>

      <h2>2. Computational Environments & Containers</h2>
      <p>
        Code that runs on your Mac today might break on your colleague's Windows machine, or even on your own Mac 
        after an OS update, due to differing library versions. We solve this by explicitly defining the environment.
      </p>
      <ul>
        <li>
          <strong>Package Managers (Conda, renv, pip):</strong> These tools create isolated environments mapping out 
          the exact versions of R, Python, and external libraries used.
        </li>
        <li>
          <strong>Containers (Docker, Singularity):</strong> Containers take isolation a step further by packaging 
          the code, the dependencies, and the operating system itself into a standardized unit. A Docker container 
          will run identically on a laptop or a massive compute cluster.
        </li>
      </ul>

      <h2>3. Workflow Managers</h2>
      <p>
        If your analysis requires running a bash script, then a Python script, then an R script, doing so manually 
        invites human error. Workflow managers automate this pipeline, turning a collection of scripts into a 
        single, executable research object.
      </p>
      
      <Callout type="info" title="Tools of the Trade: Snakemake & Nextflow">
        <p className="mb-3">
          Workflow managers like <strong>Snakemake</strong> and <strong>Nextflow</strong> use a domain-specific 
          language to define the inputs and outputs of every step in your analysis. This allows the manager to 
          map out the "Directed Acyclic Graph" (DAG) of your project.
        </p>
        <ul className="space-y-2">
          <li>
            <strong>Resilience:</strong> If a run fails at step 50 of 100, you fix the bug and resume. The manager 
            only reruns the failed step and its descendants, saving days of compute time.
          </li>
          <li>
            <strong>Portability:</strong> The same workflow can run on your laptop, a university HPC cluster, 
            or the cloud (AWS/Azure) without changing a single line of code.
          </li>
          <li>
            <strong>Native Integration:</strong> They can automatically download and deploy the specific 
            Conda environments or Docker containers required for each individual step.
          </li>
          <li>
            <strong>Self-Documenting:</strong> The workflow file itself serves as the ultimate documentation 
            of which data went into which script to produce which result.
          </li>
        </ul>
      </Callout>

      <h2>The Code is Not The Output</h2>
      <p>
        Finally, remember that sharing a PDF of your results is sharing the <em>advertisement</em> of your scholarship. 
        The actual scholarship is the data, the computing environment, and the full software suite that generated that figure.
      </p>
    </motion.div>
  );
};

export default Analysis;